import { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Loader2, Paperclip, RefreshCcw, Save, Upload, X } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api, BASE, getToken } from '@/lib/api';
import { usePageParams } from '@/hooks/usePageParams';

type QcCheckResult = 'PASS' | 'FAIL' | 'N/A' | '';

type QcGroupLine = {
  asn_line_id: string;
  qc_record_id?: string | null;
  qc_number?: string | null;
  line_number?: number | null;
  inventory_id?: string | null;
  inventory_code?: string | null;
  sku_code?: string | null;
  sku_name?: string | null;
  heat_no?: string | null;
  mill_cert_no?: string | null;
  country_of_origin?: string | null;
  inspection_qty?: number | null;
  ready_qty?: number | null;
  reject_qty?: number | null;
  na_qty?: number | null;
  line_status?: string | null;
  inspection_status?: string | null;
  putaway_location_id?: string | null;
  putaway_location_code?: string | null;
  dimension_verification?: QcCheckResult | null;
  visual_condition?: QcCheckResult | null;
  heat_no_verification?: QcCheckResult | null;
  mill_cert_verification?: QcCheckResult | null;
  material_identification?: QcCheckResult | null;
  notes?: string | null;
  attachment_count?: number;
  attachment_files?: Array<{ id: string; filename?: string | null; document_type?: string | null }>;
};

type QcGroupDetail = {
  id: string;
  asn_number?: string | null;
  source_order_number?: string | null;
  project_code?: string | null;
  supplier_name?: string | null;
  warehouse_name?: string | null;
  status?: string | null;
  asn_status?: string | null;
  is_read_only?: boolean;
  inspector_name?: string | null;
  created_at?: string | null;
  messages?: MessageLog[];
  lines: QcGroupLine[];
};

type AttachmentRow = {
  id: string;
  filename?: string | null;
  document_type?: string | null;
  mime_type?: string | null;
  size_bytes?: number | string | null;
  created_at?: string | null;
};

type MessageLog = {
  id?: string;
  channel?: string;
  from?: string;
  to?: string;
  subject?: string;
  message?: string;
  link?: string;
  createdAt?: string;
};

type LineDraft = {
  checked: boolean;
  readyQty: string;
  rejectQty: string;
  dimensionVerification: QcCheckResult;
  visualCondition: QcCheckResult;
  heatNoVerification: QcCheckResult;
  millCertVerification: QcCheckResult;
  materialIdentification: QcCheckResult;
  notes: string;
};

type Props = {
  id?: string;
  embedded?: boolean;
  onClose?: () => void;
  readOnly?: boolean;
};

function formatStatusLabel(status: string | null | undefined) {
  if (!status) return 'Unknown';
  const upper = String(status).trim().toUpperCase();
  if (upper === 'QC_PENDING') return 'QC Pending';
  if (upper === 'QC_PASSED') return 'QC Passed';
  if (upper === 'QC_PARTIAL') return 'QC Partial';
  if (upper === 'QC_FAILED') return 'QC Failed';
  return status
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function statusTone(status: string | null | undefined) {
  switch (String(status ?? '').toUpperCase()) {
    case 'QC_PASSED':
    case 'PASSED':
    case 'PUTAWAY_PENDING':
      return 'bg-green-100 text-green-700 border-green-200';
    case 'QC_FAILED':
    case 'FAILED':
    case 'REJECTED':
      return 'bg-red-100 text-red-700 border-red-200';
    case 'QC_PARTIAL':
    case 'PARTIAL':
      return 'bg-orange-100 text-orange-700 border-orange-200';
    case 'QC_PENDING':
      return 'bg-amber-100 text-amber-700 border-amber-200';
    default:
      return 'bg-amber-100 text-amber-700 border-amber-200';
  }
}

function createDraft(line: QcGroupLine): LineDraft {
  return {
    checked: false,
    readyQty: String(Number(line.ready_qty ?? 0)),
    rejectQty: String(Number(line.reject_qty ?? 0)),
    dimensionVerification: line.dimension_verification ?? '',
    visualCondition: line.visual_condition ?? '',
    heatNoVerification: line.heat_no_verification ?? '',
    millCertVerification: line.mill_cert_verification ?? '',
    materialIdentification: line.material_identification ?? '',
    notes: line.notes ?? '',
  };
}

function HeaderField({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 min-h-8 text-sm font-medium text-foreground">{value && value.trim() ? value : '-'}</div>
    </div>
  );
}

const CHECK_OPTIONS = [
  { label: 'Accept', value: 'PASS' },
  { label: 'Reject', value: 'FAIL' },
  { label: 'N/A', value: 'N/A' },
] as const;

export function WmsQcInboundDetailPanel({ id: idProp, readOnly = false }: Props) {
  const params = usePageParams<{ id: string }>();
  const id = idProp ?? params.id;
  const [detail, setDetail] = useState<QcGroupDetail | null>(null);
  const [drafts, setDrafts] = useState<Record<string, LineDraft>>({});
  const [inspectorName, setInspectorName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const [qcAttachments, setQcAttachments] = useState<AttachmentRow[]>([]);
  const [error, setError] = useState('');
  const [validationMessage, setValidationMessage] = useState('');

  const fetchQcAttachments = useCallback(async () => {
    if (!id) return;
    const rows = await api.get<{ items?: AttachmentRow[] }>(
      `/wms/attachments?referenceType=${encodeURIComponent('QC_GROUP')}&referenceId=${encodeURIComponent(id)}`,
    );
    setQcAttachments(rows.items ?? []);
  }, [id]);

  const fetchDetail = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.get<QcGroupDetail>(`/wms/qc-groups/${encodeURIComponent(id)}`);
      setDetail(res);
      setInspectorName(res.inspector_name ?? '');
      setDrafts(Object.fromEntries((res.lines ?? []).map((line) => [line.asn_line_id, createDraft(line)])));
      setValidationMessage('');
      await fetchQcAttachments();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load QC detail');
      setDetail(null);
      setDrafts({});
      setQcAttachments([]);
    } finally {
      setLoading(false);
    }
  }, [fetchQcAttachments, id]);

  useEffect(() => {
    void fetchDetail();
  }, [fetchDetail]);

  const dirty = useMemo(() => {
    if (!detail) return false;
    return detail.lines.some((line) => {
      const draft = drafts[line.asn_line_id] ?? createDraft(line);
      return JSON.stringify(draft) !== JSON.stringify(createDraft(line));
    }) || inspectorName !== (detail.inspector_name ?? '');
  }, [detail, drafts, inspectorName]);

  const patchLine = (lineId: string, patch: Partial<LineDraft>) => {
    setDrafts((current) => ({ ...current, [lineId]: { ...(current[lineId] ?? createDraft({ asn_line_id: lineId })), ...patch } }));
  };

  const selectedLineIds = useMemo(
    () => Object.entries(drafts).filter(([, draft]) => draft.checked).map(([lineId]) => lineId),
    [drafts],
  );
  const setSelectedReady = () => {
    if (!detail) return;
    setDrafts((current) => {
      const next = { ...current };
      for (const line of detail.lines) {
        const draft = next[line.asn_line_id] ?? createDraft(line);
        if (!draft.checked) continue;
        const inspectionQty = Number(line.inspection_qty ?? 0);
        next[line.asn_line_id] = {
          ...draft,
          readyQty: String(inspectionQty),
          rejectQty: '0',
          dimensionVerification: 'PASS',
          visualCondition: 'PASS',
          heatNoVerification: 'PASS',
          millCertVerification: 'PASS',
          materialIdentification: 'PASS',
        };
      }
      return next;
    });
  };

  const saveAll = async () => {
    if (!detail) return;
    setValidationMessage('');
    try {
      const payloadLines = detail.lines.map((line) => {
        const draft = drafts[line.asn_line_id] ?? createDraft(line);
        const inspectionQty = Number(line.inspection_qty ?? 0);
        const qtyPassed = Number(draft.readyQty || 0);
        const qtyFailed = Number(draft.rejectQty || 0);
        const qtyNa = 0;
        const hasInspectionResult = qtyPassed !== 0 || qtyFailed !== 0;
        if (!Number.isFinite(qtyPassed) || !Number.isFinite(qtyFailed)) {
          throw new Error(`Line ${line.line_number ?? '-'} quantity must be a valid number`);
        }
        if (hasInspectionResult && qtyPassed + qtyFailed !== inspectionQty) {
          throw new Error(`Line ${line.line_number ?? '-'} passed + rejected must equal inspected qty`);
        }
        if (hasInspectionResult && (
          !draft.dimensionVerification
          || !draft.materialIdentification
          || !draft.visualCondition
          || !draft.heatNoVerification
        )) {
          throw new Error(`Line ${line.line_number ?? '-'} Dimension, Spec, Visual and Heat/Cert No Check are required`);
        }
        return {
          asnLineId: line.asn_line_id,
          qtyPassed,
          qtyFailed,
          qtyNa,
          dimensionVerification: draft.dimensionVerification || undefined,
          visualCondition: draft.visualCondition || undefined,
          heatNoVerification: draft.heatNoVerification || undefined,
          millCertVerification: draft.heatNoVerification || undefined,
          materialIdentification: draft.materialIdentification || undefined,
          notes: draft.notes.trim() || undefined,
        };
      });

      setSaving(true);
      await api.patch(`/wms/qc-groups/${encodeURIComponent(detail.id)}`, {
        inspectorName: inspectorName.trim() || undefined,
        lines: payloadLines,
      });
      toast.success(`Inbound ${detail.asn_number ?? detail.id} - QC Completed`);
      await fetchDetail();
    } catch (err: any) {
      const message = err?.message ?? 'Failed to save QC';
      setValidationMessage(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const openAttachment = async (attachmentId: string) => {
    const response = await fetch(`${BASE}/wms/attachments/${encodeURIComponent(attachmentId)}/content`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!response.ok) {
      throw new Error('Failed to open attachment');
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const uploadQcAttachments = async (files: FileList | null) => {
    if (!files || files.length === 0 || !detail) return;
    setUploadingAttachments(true);
    try {
      for (const file of Array.from(files)) {
        const form = new FormData();
        form.append('file', file);
        form.append('referenceType', 'QC_GROUP');
        form.append('referenceId', detail.id);
        form.append('documentType', 'QC_ATTACHMENT');
        const response = await fetch(`${BASE}/wms/attachments`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${getToken()}` },
          body: form,
        });
        if (!response.ok) {
          const payload = await response.json().catch(() => null);
          throw new Error(payload?.error?.message ?? 'Failed to upload QC attachment');
        }
      }
      toast.success('QC attachment uploaded');
      await fetchQcAttachments();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to upload QC attachment');
    } finally {
      setUploadingAttachments(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading QC detail...
      </div>
    );
  }

  if (error || !detail) {
    return <div className="p-6 text-sm text-destructive">{error || 'QC detail not found.'}</div>;
  }

  const locked = readOnly || detail.is_read_only === true || String(detail.status ?? '').toUpperCase() === 'QC_PASSED';
  const canUploadQcAttachments = !readOnly && !locked;

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <div className="shrink-0 border-b px-6 py-4">
        <div>
          <div className="pr-10">
            <h2 className="text-lg font-semibold tracking-tight">QC : {detail.asn_number ?? '-'}</h2>
            <p className="mt-1 text-sm text-muted-foreground">Inbound QC detail</p>
            <div className="mt-2">
              <Badge className={`border ${statusTone(detail.status)}`}>{formatStatusLabel(detail.status)}</Badge>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-start gap-2">
            <Button variant="outline" onClick={() => void fetchDetail()} disabled={loading || saving}>
              <RefreshCcw className="mr-1.5 h-3.5 w-3.5" />
              Refresh
            </Button>
            {!locked ? (
              <>
                <Button variant="outline" onClick={setSelectedReady} disabled={selectedLineIds.length === 0 || saving}>
                  <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
                  Set Ready
                </Button>
                <Button onClick={() => void saveAll()} disabled={saving || !dirty}>
                  {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1.5 h-3.5 w-3.5" />}
                  Save All
                </Button>
              </>
            ) : null}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-6 gap-4">
          <HeaderField label="Inbound No" value={detail.asn_number ?? '-'} />
          <HeaderField label="PO / Ref" value={detail.source_order_number ?? '-'} />
          <HeaderField label="Project" value={detail.project_code ?? '-'} />
          <HeaderField label="Warehouse" value={detail.warehouse_name ?? '-'} />
          <HeaderField label="Supplier" value={detail.supplier_name ?? '-'} />
          <div>
            <Label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Inspected By</Label>
            {locked ? (
              <div className="mt-1 min-h-9 rounded-md border bg-muted/30 px-3 py-2 text-sm text-foreground">
                {inspectorName.trim() || '-'}
              </div>
            ) : (
              <Input className="mt-1" value={inspectorName} onChange={(event) => setInspectorName(event.target.value)} placeholder="Inspector name" />
            )}
          </div>
        </div>
      </div>

      {canUploadQcAttachments ? (
        <div className="shrink-0 border-b bg-slate-50/60 px-6 py-3">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Paperclip className="h-4 w-4" />
                QC Attachments
              </div>
              {qcAttachments.length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  {qcAttachments.map((attachment) => (
                    <button
                      key={attachment.id}
                      type="button"
                      className="rounded-md border bg-background px-2.5 py-1 text-xs text-primary underline-offset-2 hover:underline"
                      onClick={() => void openAttachment(attachment.id)}
                    >
                      {attachment.filename ?? attachment.id}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="mt-1 text-xs text-muted-foreground">No QC attachments uploaded.</div>
              )}
            </div>
            <label className="inline-flex h-9 cursor-pointer items-center justify-center rounded-md border bg-background px-3 text-sm font-medium hover:bg-muted">
              {uploadingAttachments ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Upload className="mr-1.5 h-3.5 w-3.5" />}
              Upload Files
              <input
                type="file"
                multiple
                className="hidden"
                disabled={uploadingAttachments}
                onChange={(event) => {
                  void uploadQcAttachments(event.target.files);
                  event.currentTarget.value = '';
                }}
              />
            </label>
          </div>
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-hidden px-6 py-4">
        {validationMessage ? (
          <div className="mb-4 flex items-start justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-base font-medium text-red-700">
            <div>{validationMessage}</div>
            <button type="button" className="shrink-0" onClick={() => setValidationMessage('')}>
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : null}
        <div className="flex h-full min-h-0 flex-col overflow-hidden">
          <div className="mb-2 shrink-0 text-sm font-semibold">Lines</div>
          <div className="runtime-grid-scrollbar boox-x-scroll min-h-0 flex-1 overflow-auto rounded-md border">
          <table className="min-w-[2200px] w-full text-sm">
            <thead className="sticky top-0 z-10 bg-background">
              <tr className="border-b bg-slate-50">
                {!locked ? <th className="px-3 py-2 text-left font-medium">Pick</th> : null}
                <th className="px-3 py-2 text-left font-medium">Line</th>
                <th className="px-3 py-2 text-left font-medium">QC Code</th>
                <th className="px-3 py-2 text-left font-medium">Inventory Code</th>
                <th className="px-3 py-2 text-left font-medium">SKU Code</th>
                <th className="px-3 py-2 text-left font-medium">Description</th>
                <th className="px-3 py-2 text-left font-medium">Heat No</th>
                <th className="px-3 py-2 text-left font-medium">Origin</th>
                <th className="px-3 py-2 text-left font-medium">Mill Cert</th>
                <th className="px-3 py-2 text-left font-medium">Inspected Qty</th>
                <th className="px-3 py-2 text-left font-medium">Passed Qty</th>
                <th className="px-3 py-2 text-left font-medium">Rejected Qty</th>
                <th className="px-3 py-2 text-left font-medium">Dimension</th>
                <th className="px-3 py-2 text-left font-medium">Spec</th>
                <th className="px-3 py-2 text-left font-medium">Visual</th>
                <th className="px-3 py-2 text-left font-medium">Heat/Cert No Check</th>
                <th className="px-3 py-2 text-left font-medium">Remark</th>
                <th className="px-3 py-2 text-left font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {detail.lines.map((line) => {
                const draft = drafts[line.asn_line_id] ?? createDraft(line);
                const attachmentLabel = line.attachment_count ? `${line.attachment_count} file(s)` : 'Missing';
                return (
                  <tr key={line.asn_line_id} className="border-b align-top">
                    {!locked ? (
                      <td className="px-3 py-2">
                        <Checkbox checked={draft.checked} onCheckedChange={(checked) => patchLine(line.asn_line_id, { checked: checked === true })} />
                      </td>
                    ) : null}
                    <td className="px-3 py-2">{line.line_number ?? '-'}</td>
                    <td className="px-3 py-2 font-medium">{line.qc_number ?? '-'}</td>
                    <td className="px-3 py-2 font-medium">{line.inventory_code ?? '-'}</td>
                    <td className="px-3 py-2">{line.sku_code ?? '-'}</td>
                    <td className="px-3 py-2">{line.sku_name ?? '-'}</td>
                    <td className="px-3 py-2">{line.heat_no ?? '-'}</td>
                    <td className="px-3 py-2">{line.country_of_origin ?? '-'}</td>
                    <td className="px-3 py-2">
                      <div className={line.attachment_count ? 'text-foreground' : 'font-medium text-red-600'}>{attachmentLabel}</div>
                      {line.mill_cert_no ? <div className="text-xs text-muted-foreground">No: {line.mill_cert_no}</div> : null}
                      {line.attachment_files?.slice(0, 2).map((attachment) => (
                        <button
                          key={attachment.id}
                          type="button"
                          className="block text-left text-xs text-primary underline-offset-2 hover:underline"
                          onClick={() => void openAttachment(attachment.id)}
                        >
                          {attachment.filename ?? attachment.id}
                        </button>
                      ))}
                    </td>
                    <td className="px-3 py-2">{Number(line.inspection_qty ?? 0).toLocaleString()}</td>
                    <td className="px-3 py-2">
                      {locked ? <div className="text-sm">{draft.readyQty}</div> : <Input type="number" min="0" value={draft.readyQty} onChange={(event) => patchLine(line.asn_line_id, { readyQty: event.target.value })} />}
                    </td>
                    <td className="px-3 py-2">
                      {locked ? <div className="text-sm">{draft.rejectQty}</div> : <Input type="number" min="0" value={draft.rejectQty} onChange={(event) => patchLine(line.asn_line_id, { rejectQty: event.target.value })} />}
                    </td>
                    <td className="px-3 py-2">
                      {locked ? <div className="text-sm">{draft.dimensionVerification || '-'}</div> : (
                        <Select value={draft.dimensionVerification || undefined} onValueChange={(value) => patchLine(line.asn_line_id, { dimensionVerification: value as QcCheckResult })}>
                          <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>{CHECK_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
                        </Select>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {locked ? <div className="text-sm">{draft.materialIdentification || '-'}</div> : (
                        <Select value={draft.materialIdentification || undefined} onValueChange={(value) => patchLine(line.asn_line_id, { materialIdentification: value as QcCheckResult })}>
                          <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>{CHECK_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
                        </Select>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {locked ? <div className="text-sm">{draft.visualCondition || '-'}</div> : (
                        <Select value={draft.visualCondition || undefined} onValueChange={(value) => patchLine(line.asn_line_id, { visualCondition: value as QcCheckResult })}>
                          <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>{CHECK_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
                        </Select>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {locked ? <div className="text-sm">{draft.heatNoVerification || '-'}</div> : (
                        <Select value={draft.heatNoVerification || undefined} onValueChange={(value) => patchLine(line.asn_line_id, { heatNoVerification: value as QcCheckResult })}>
                          <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>{CHECK_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
                        </Select>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {locked ? <div className="text-sm">{draft.notes || '-'}</div> : (
                        <Input value={draft.notes} onChange={(event) => patchLine(line.asn_line_id, { notes: event.target.value })} placeholder="Remark" />
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Badge className={`border ${statusTone(line.inspection_status ?? line.line_status)}`}>{formatStatusLabel(line.inspection_status ?? line.line_status)}</Badge>
                    </td>
                  </tr>
                );
              })}
              {detail.lines.length === 0 ? (
                <tr>
                  <td colSpan={locked ? 18 : 19} className="px-4 py-10 text-center text-sm text-muted-foreground">
                    No QC lines found.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        </div>
      </div>
    </div>
  );
}

export default WmsQcInboundDetailPanel;
